import React, { useState, useEffect } from 'react';
import { 
  Box, Heading, VStack, Text, Button, HStack, Icon, Avatar, Textarea, 
  useToast, Divider, Spinner, Progress, Link as ChakraLink, Image,
  Popover, PopoverTrigger, PopoverContent,useDisclosure, PopoverArrow, PopoverCloseButton, PopoverBody, IconButton, Spacer 
} from '@chakra-ui/react';
import { useAuthCliente } from '../context/AuthClienteContext';
import { useComments } from '../hooks/useComments';
import { useMembers } from '../hooks/useMembers';
import { db } from '../firebase/firebase-config';
import { collection, addDoc, serverTimestamp, doc, setDoc, writeBatch, onSnapshot } from 'firebase/firestore';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale/es';
import Carousel from 'framer-motion-carousel';
import { MentionsInput, Mention } from 'react-mentions';
import { FaPoll, FaSmile } from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';
import EmojiPicker from 'emoji-picker-react';
import '../styles/mentions-input.css';
import PlayerMicroProfileModal from './PlayerMicroProfileModal';

function usePollResults(announcementId) {
    const { currentUser } = useAuthCliente();
    const [results, setResults] = useState({});
    const [totalVotes, setTotalVotes] = useState(0);
    const [userVote, setUserVote] = useState(null);

    useEffect(() => {
        if (!currentUser || !announcementId) return;
        const votesRef = collection(db, 'companies', currentUser.companyId, 'announcements', announcementId, 'votes');
        const unsubscribe = onSnapshot(votesRef, (snapshot) => {
            const voteCounts = {};
            let total = 0;
            let foundUserVote = null;
            snapshot.forEach(doc => {
                const vote = doc.data();
                voteCounts[vote.optionIndex] = (voteCounts[vote.optionIndex] || 0) + 1;
                total++;
                if (doc.id === currentUser.uid) {
                    foundUserVote = vote.optionIndex;
                }
            });
            setResults(voteCounts);
            setTotalVotes(total);
            setUserVote(foundUserVote);
        });
        return () => unsubscribe();
    }, [currentUser, announcementId]);

    return { results, totalVotes, userVote };
}

function Poll({ announcement }) {
    const { currentUser } = useAuthCliente();
    const toast = useToast();
    const { results, totalVotes, userVote } = usePollResults(announcement.id);
    const voted = userVote !== null;
    const isDeadlinePassed = announcement.poll.deadline ? new Date() > announcement.poll.deadline.toDate() : false;

    const handleVote = async (optionIndex) => {
        if (voted || isDeadlinePassed) return;
        const voteRef = doc(db, 'companies', currentUser.companyId, 'announcements', announcement.id, 'votes', currentUser.uid);
        try {
            await setDoc(voteRef, { optionIndex });
            toast({ title: "¡Voto registrado!", status: 'success' });
        } catch (error) {
            toast({ title: "Error al votar", description: error.message, status: 'error' });
        }
    };

    return (
        <Box mt={4} p={4} borderWidth="1px" borderColor="blue.200" borderRadius="md">
            <Heading size="md" mb={3}><Icon as={FaPoll} mr={2}/>{announcement.poll.question}</Heading>
            <VStack align="stretch" spacing={3}>
                {announcement.poll.options.map((option, index) => {
                    const votes = results[index] || 0;
                    const percentage = totalVotes > 0 ? (votes / totalVotes) * 100 : 0;
                    const isSelected = userVote === index;
                    return (
                        <Button 
                            key={index} 
                            onClick={() => handleVote(index)} 
                            isDisabled={voted || isDeadlinePassed}
                            variant="outline" 
                            justifyContent="flex-start" 
                            position="relative" 
                            overflow="hidden"
                            borderColor={isSelected ? "green.500" : "gray.200"}
                            borderWidth={isSelected ? "2px" : "1px"}
                        >
                            {voted && <Progress value={percentage} size="md" colorScheme="green" position="absolute" top="0" left="0" w="full" h="full" opacity={0.3} />}
                            <HStack w="full" justify="space-between" zIndex={1}>
                                <Text>{option}</Text>
                                {voted && <Text fontWeight="bold">{percentage.toFixed(0)}% ({votes})</Text>}
                            </HStack>
                        </Button>
                    );
                })}
            </VStack>
            {isDeadlinePassed && !voted && (
                <Text fontSize="sm" color="red.500" mt={3} textAlign="center">
                    La votación para esta encuesta ha cerrado.
                </Text>
            )}
        </Box>
    );
}

// --- Sub-componente para la caja de comentarios ---
function CommentInput({ announcementId, announcementTitle }) {
    const { currentUser } = useAuthCliente();
    const { members } = useMembers();
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toast = useToast();
    const MAX_CHARS = 280;

   const handlePostComment = async () => {
    if (!comment.trim() || !currentUser) return;
    setIsSubmitting(true);
    
    // La referencia apunta a la sub-colección de comentarios del anuncio
    const ref = collection(db, 'companies', currentUser.companyId, 'announcements', announcementId, 'comments');
    
    try {
        // Ahora solo creamos el documento del comentario
        await addDoc(ref, {
            text: comment,
            authorId: currentUser.uid,
            authorName: currentUser.name,
            authorAvatar: currentUser.photoURL || '',
            createdAt: serverTimestamp()
        });

        setComment('');
        toast({ title: 'Comentario publicado', status: 'success' });
    } catch (error) {
        toast({ title: 'Error al publicar', description: error.message, status: 'error' });
    } finally {
        setIsSubmitting(false);
    }
};
    
    // ✅ VERSIÓN SIMPLIFICADA Y CORREGIDA DE LOS ESTILOS
     const suggestionsStyle = {
        list: {
            backgroundColor: 'white',
            border: '1px solid rgba(0,0,0,0.15)',
            borderRadius: '0.375rem',
            boxShadow: 'md',
            fontSize: '14px',
        },
        item: {
            padding: '5px 15px',
            '&focused': { backgroundColor: '#f0f0f0' },
        },
    };
    
const filteredMembers = members.filter(member => member.id !== currentUser.uid);

    return (
        <VStack align="stretch" pt={4}>
            {/* Contenedor que aplica los estilos del CSS */}
            <Box className="mentions-input-box" borderWidth="1px" borderRadius="md" borderColor="gray.200">
                <MentionsInput
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Escribe un comentario... Usa @ para mencionar."
                    style={{}} // El CSS externo maneja los estilos principales
                    className="mentions-input" // Clase para aplicar estilos
                >
                    <Mention
                        trigger="@"
                        data={filteredMembers}
                        style={{ backgroundColor: '#e6f3ff' }}
                        markup="@[__display__](__id__)"
                        className="mentions-input__mention"
                    />
                </MentionsInput>
            </Box>

            {/* Barra de acciones con layout corregido */}
            <HStack w="full" mt={2}>
                <Popover>
                    <PopoverTrigger>
                        <IconButton icon={<FaSmile />} size="sm" colorScheme="blue" variant="solid" aria-label="Añadir emoji" />
                    </PopoverTrigger>
                    <PopoverContent border="none" boxShadow="lg">
                        <PopoverArrow />
                        <PopoverBody p={0}><EmojiPicker onEmojiClick={(emojiData) => setComment(prev => prev + emojiData.emoji)} width="100%" /></PopoverBody>
                    </PopoverContent>
                </Popover>
                <Spacer />
                <Text fontSize="xs" color={comment.length > MAX_CHARS ? "red.500" : "gray.500"}>
                    {MAX_CHARS - comment.length} rest.
                </Text>
                <Button colorScheme="green" size="sm" onClick={handlePostComment} isLoading={isSubmitting} isDisabled={comment.length > MAX_CHARS || !comment.trim()}>
                    Publicar Comentario
                </Button>
            </HStack>
        </VStack>
    );
}

const CommentRenderer = ({ text }) => {
  const parts = text.split(/(@\[[^\]]+\]\(\S+\))/g);

  return (
    <Text as="span" fontSize="sm">
      {parts.map((part, index) => {
        const mentionMatch = /@\[([^\]]+)\]\((\S+)\)/.exec(part);
        if (mentionMatch) {
          const name = mentionMatch[1];
          const id = mentionMatch[2];
          // Aquí podríamos usar un RouterLink si quisiéramos que el nombre fuera un enlace al perfil
          return (
            <Text as="span" key={index} color="blue.500" fontWeight="bold">
              @{name}
            </Text>
          );
        }
        return part;
      })}
    </Text>
  );
};


// --- Componente principal de la tarjeta de anuncio ---
export function AnnouncementCard({ announcement }) {
    const { comments, loading: loadingComments } = useComments(announcement.id);
    const { members } = useMembers(); // Obtenemos la lista completa de miembros

    // LÓGICA PARA CONTROLAR EL MICRO MODAL
    const { isOpen, onOpen, onClose } = useDisclosure();
    const [selectedPlayer, setSelectedPlayer] = useState(null);

    const handleViewProfile = (authorId) => {
        const memberData = members.find(m => m.id === authorId);
        if (memberData) {
            setSelectedPlayer(memberData);
            onOpen();
        }
    };

    return (
        <>
            <PlayerMicroProfileModal isOpen={isOpen} onClose={onClose} member={selectedPlayer} />

            <Box bg="white" p={{base: 4, md: 6}} borderRadius="xl" boxShadow="lg">
                <VStack align="stretch" spacing={3}>
                    {announcement.imageUrls && announcement.imageUrls.length > 0 && (
                        <Box borderRadius="md" overflow="hidden">
                            <Carousel>
                                {announcement.imageUrls.map((url, i) => (
                                    <ChakraLink key={i} href={url} isExternal>
                                        <Image src={url} alt={`Imagen ${i + 1}`} w="full" h={{base: "250px", md: "400px"}} objectFit="cover" />
                                    </ChakraLink>
                                ))}
                            </Carousel>
                        </Box>
                    )}
                    <Heading size="lg">{announcement.title}</Heading>
                    <HStack wrap="wrap">
                        <Text fontSize="sm" fontWeight="bold">Publicado por {announcement.authorName}</Text>
                        <Text fontSize="sm" color="gray.500">• {announcement.createdAt ? format(announcement.createdAt.toDate(), "d MMM, yyyy 'a las' HH:mm'h'", { locale: es }) : ''}</Text>
                        {announcement.updatedAt && (
                            <Text fontSize="sm" color="gray.500" fontStyle="italic">(editado {format(announcement.updatedAt.toDate(), "d MMM, yyyy 'a las' HH:mm'h'", { locale: es })})</Text>
                        )}
                    </HStack>
                    <Text whiteSpace="pre-wrap">{announcement.content}</Text>

                    {announcement.poll && <Poll announcement={announcement} />}
                    
                    <Divider pt={4}/>
                    <Heading size="md" color="gray.700">Comentarios ({comments.length})</Heading>
                    {loadingComments ? <Spinner /> : comments.map(comment => (
                        <HStack key={comment.id} align="start" spacing={3} bg="gray.50" p={3} borderRadius="md">
                            <Avatar 
                                size="sm" 
                                name={comment.authorName} 
                                src={comment.authorAvatar}
                                cursor="pointer"
                                onClick={() => handleViewProfile(comment.authorId)}
                            />
                            <VStack align="start" spacing={1}>
                                <HStack>
                                    <Text 
                                        fontWeight="bold" 
                                        fontSize="sm" 
                                        cursor="pointer"
                                        _hover={{ textDecoration: 'underline' }}
                                        onClick={() => handleViewProfile(comment.authorId)}
                                    >
                                        {comment.authorName}
                                    </Text>
                                    <Text fontSize="xs" color="gray.400">• {comment.createdAt ? formatDistanceToNow(comment.createdAt.toDate(), { addSuffix: true, locale: es }) : ''}</Text>
                                </HStack>
                                <CommentRenderer text={comment.text} />
                            </VStack>
                        </HStack>
                    ))}
                    <CommentInput announcementId={announcement.id} announcementTitle={announcement.title} />
                </VStack>
            </Box>
        </>
    );
}